/**
 * Credentials Preview (dev only)
 *
 * Renders the new identity-credential components against canned fixtures
 * so the visual layer can be validated without backend connectivity or a
 * logged-in session. The mutation hooks the components call still work
 * against the real API — clicking Revoke / Test / Reconnect here will
 * fail since there's no auth cookie, which is expected for visual
 * verification. Visit `/dev/credentials-preview` in dev mode.
 */

import { useState } from 'react';
import { Button } from '@/components/ui';
import ProviderCard from '@/pages/credentials/ProviderCard';
import AvailableProvidersList from '@/pages/credentials/AvailableProvidersList';
import AddApiKeyModal from '@/pages/credentials/AddApiKeyModal';
import DeviceAuthModal from '@/pages/credentials/DeviceAuthModal';
import type {
  Credential,
  OAuthDeviceStart,
  ProviderSummary,
} from '@/types/identity';
import type { Scope } from '@/pages/credentials/scope';

const PERSONAL: Scope = { kind: 'user' };

const ORG: Scope = { kind: 'org' };

const GOOGLE: ProviderSummary = {
  id: 'google',
  displayName: 'Google',
  supportedAuthTypes: ['oauth2'],
  consumableAs: ['platform'],
  oauthAppMode: 'centralized',
  connectionCount: 2,
  status: 'connected',
};

const ATLASSIAN: ProviderSummary = {
  id: 'atlassian',
  displayName: 'Atlassian',
  supportedAuthTypes: ['oauth2', 'api_key'],
  consumableAs: ['platform'],
  oauthAppMode: 'centralized',
  connectionCount: 1,
  status: 'connected',
  apiKeySetupInstructions:
    'Generate an API token at id.atlassian.com → Security → API tokens. Paste it together with your Atlassian account email and site URL.',
};

const SLACK: ProviderSummary = {
  id: 'slack',
  displayName: 'Slack',
  supportedAuthTypes: ['oauth2'],
  consumableAs: ['platform'],
  oauthAppMode: 'centralized',
  connectionCount: 1,
  status: 'needs_reauth',
};

// Slack's app/bot-token flow: the user connection stays OAuth-only, but the
// org/space bot token is a separate api-key credential. `apiKeySetupInstructions`
// carries the required bot scopes, rendered inside the add modal.
const SLACK_APP: ProviderSummary = {
  id: 'slack',
  displayName: 'Slack',
  supportedAuthTypes: ['oauth2'],
  consumableAs: ['platform', 'mcp'],
  oauthAppMode: 'centralized',
  connectionCount: 0,
  status: 'disconnected',
  apiKeySetupInstructions:
    'In your Slack app under OAuth & Permissions, add these bot scopes, then install to the workspace and copy the Bot User OAuth Token:\n• chat:write\n• chat:write.public\n• reactions:write\n• channels:read',
};

const TRELLO: ProviderSummary = {
  id: 'trello',
  displayName: 'Trello',
  supportedAuthTypes: ['api_key'],
  consumableAs: ['platform'],
  oauthAppMode: null,
  connectionCount: 0,
  status: 'disconnected',
  apiKeySetupInstructions:
    'Obtain an app key at trello.com/app-key, then generate a user token against it.',
};

const GITLAB: ProviderSummary = {
  id: 'gitlab',
  displayName: 'GitLab',
  supportedAuthTypes: ['api_key'],
  consumableAs: ['platform'],
  oauthAppMode: null,
  connectionCount: 0,
  status: 'disconnected',
};

const X: ProviderSummary = {
  id: 'x',
  displayName: 'X',
  supportedAuthTypes: ['oauth2'],
  consumableAs: ['platform'],
  oauthAppMode: 'centralized',
  connectionCount: 0,
  status: 'disconnected',
};

const AWS_SSO: ProviderSummary = {
  id: 'aws-sso',
  displayName: 'AWS IAM Identity Center',
  supportedAuthTypes: ['oauth2'],
  consumableAs: ['platform'],
  oauthAppMode: 'centralized',
  connectionCount: 3,
  status: 'connected',
};

// Datadog ships as two provider-wide rows — one per auth method — which is the
// new multi-row contract. Both carry `mcpServerUrl` and no `providerInstanceId`.
const DATADOG_MCP_URL = 'https://mcp.datadoghq.com/api/unstable/mcp-server/mcp';

const DATADOG_OAUTH_MANUAL: ProviderSummary = {
  id: 'datadog',
  displayName: 'Datadog',
  supportedAuthTypes: ['oauth2_manual'],
  consumableAs: ['mcp'],
  oauthAppMode: null,
  connectionCount: 1,
  status: 'connected',
  mcpServerUrl: DATADOG_MCP_URL,
};

const DATADOG_API_KEY: ProviderSummary = {
  id: 'datadog',
  displayName: 'Datadog',
  supportedAuthTypes: ['api_key'],
  consumableAs: ['mcp'],
  oauthAppMode: null,
  connectionCount: 0,
  status: 'disconnected',
  mcpServerUrl: DATADOG_MCP_URL,
  apiKeySetupInstructions:
    'Create an API key and an Application key in Datadog → Organization Settings.',
  apiKeyHeaders: [
    { name: 'DD-API-KEY', label: 'API key', secret: true },
    { name: 'DD-APPLICATION-KEY', label: 'Application key', secret: true },
  ],
};

// Device-authorization start payload as aws-sso's `/start` route returns it.
const AWS_SSO_DEVICE: OAuthDeviceStart = {
  flow: 'device',
  verificationUriComplete: 'https://d-9267b2b155.awsapps.com/start/#/device?user_code=KXML-WZPT',
  userCode: 'KXML-WZPT',
  expiresIn: 557,
  interval: 1,
};

const FOURTEEN_DAYS_OUT = Date.now() + 14 * 86_400_000;
const ONE_HOUR_OUT = Date.now() + 60 * 60_000;
const FIFTY_MIN_OUT = Date.now() + 50 * 60_000;

function cred(over: Partial<Credential>): Credential {
  return {
    credId: 'cred-mock',
    orgId: 'org-1',
    principal: { kind: 'user', id: 'usr-1' },
    provider: 'google',
    authType: 'oauth2',
    status: 'connected',
    metadata: {},
    createdAt: '2026-05-01T00:00:00Z',
    updatedAt: '2026-05-01T00:00:00Z',
    ...over,
  };
}

const GOOGLE_TWO_DEFAULTED: Credential[] = [
  cred({
    credId: 'cred-g-personal',
    provider: 'google',
    expiresAt: FOURTEEN_DAYS_OUT,
    metadata: {
      accountName: 'Personal',
      externalUsername: 'jane@gmail.com',
      isDefault: true,
    },
  }),
  cred({
    credId: 'cred-g-work',
    provider: 'google',
    expiresAt: FOURTEEN_DAYS_OUT,
    metadata: {
      accountName: 'Work',
      externalUsername: 'jane@acme.com',
    },
  }),
];

const GOOGLE_TWO_NO_DEFAULT: Credential[] = GOOGLE_TWO_DEFAULTED.map((c) => ({
  ...c,
  metadata: { ...c.metadata, isDefault: false },
}));

const ATLASSIAN_ONE: Credential[] = [
  cred({
    credId: 'cred-atl-1',
    provider: 'atlassian',
    expiresAt: FOURTEEN_DAYS_OUT,
    metadata: {
      accountName: 'acme.atlassian.net',
      externalUsername: 'jane@acme.com',
      isDefault: true,
      cloudId: 'cloud-123',
      siteUrl: 'https://acme.atlassian.net',
    },
  }),
];

const SLACK_NEEDS_REAUTH: Credential[] = [
  cred({
    credId: 'cred-slack-1',
    provider: 'slack',
    status: 'needs_reauth',
    metadata: {
      accountName: 'Sales Inbox',
      externalUsername: 'jane@acme.com',
      isDefault: true,
    },
  }),
];

const DATADOG_OAUTH_CONNECTED: Credential[] = [
  cred({
    credId: 'cred-dd-oauth',
    provider: 'datadog',
    authType: 'oauth2_manual',
    metadata: { accountName: 'ARE Team' },
  }),
];

const DATADOG_API_KEY_CONNECTED: Credential[] = [
  cred({
    credId: 'cred-dd-apikey',
    provider: 'datadog',
    authType: 'api_key',
    metadata: { accountName: 'Prod monitors' },
  }),
];

// aws-sso credentials arrive as `type: 'session'` rows with a backend-synthesized
// top-level `displayName`. The login session carries only `{ region, startUrl }`
// in metadata, so without `displayName` it would fall through to "Untitled
// account". Role sessions prefer their resolved alias; the unresolved one falls
// back to the `"<accountId> - <roleName>"` backend label.
const AWS_SSO_SESSIONS: Credential[] = [
  cred({
    credId: 'crd-awssso-login',
    provider: 'aws-sso',
    providerInstanceId: 'd-9066709257.awsapps.com',
    // ~1h access-token expiry; the scheduler re-mints ~10 min before, so the
    // login row reads "Valid 1h · auto-refreshes in 50m".
    expiresAt: ONE_HOUR_OUT,
    nextRefreshAt: FIFTY_MIN_OUT,
    metadata: { region: 'us-east-1', startUrl: 'https://d-9066709257.awsapps.com/start' },
    type: 'session',
    sessionKind: 'login',
    displayName: 'AWS IAM Identity Center — d-9066709257.awsapps.com',
  }),
  cred({
    credId: 'crd-awssso-role-named',
    provider: 'aws-sso',
    providerInstanceId: '025044154040#AdministratorAccess',
    expiresAt: FOURTEEN_DAYS_OUT,
    metadata: { accountName: 'natera-ux-ai-4-prod', accountId: '025044154040', roleName: 'AdministratorAccess' },
    type: 'session',
    sessionKind: 'role',
    displayName: 'natera-ux-ai-4-prod (025044154040) - AdministratorAccess',
  }),
  cred({
    credId: 'crd-awssso-role-unnamed',
    provider: 'aws-sso',
    providerInstanceId: '110044150099#ReadOnly',
    expiresAt: FOURTEEN_DAYS_OUT,
    metadata: { accountId: '110044150099', roleName: 'ReadOnly' },
    type: 'session',
    sessionKind: 'role',
    displayName: '110044150099 - ReadOnly',
  }),
];

export default function CredentialsPreviewPage() {
  const [modalProvider, setModalProvider] = useState<ProviderSummary | null>(null);
  const [deviceOpen, setDeviceOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-4xl space-y-10">
        <header>
          <h1 className="text-2xl font-bold">Credentials components preview</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Fixture-rendered views of the new identity-credential surface.
            Backend mutations here will fail without auth — this page exists
            only for visual verification.
          </p>
        </header>

        <Section title="Provider with one account">
          <ProviderCard
            provider={ATLASSIAN}
            authType="oauth2"
            credentials={ATLASSIAN_ONE}
            canManage={true}
            scope={PERSONAL}
            showMethod={false}
            alsoAtOrg={false}
          />
        </Section>

        <Section title="Provider with two accounts (one default)">
          <ProviderCard
            provider={GOOGLE}
            authType="oauth2"
            credentials={GOOGLE_TWO_DEFAULTED}
            canManage={true}
            scope={PERSONAL}
            showMethod={false}
            alsoAtOrg={false}
          />
        </Section>

        <Section title="Two accounts, none default (warning banner)">
          <ProviderCard
            provider={GOOGLE}
            authType="oauth2"
            credentials={GOOGLE_TWO_NO_DEFAULT}
            canManage={true}
            scope={PERSONAL}
            showMethod={false}
            alsoAtOrg={false}
          />
        </Section>

        <Section title="Needs-reauth status pill">
          <ProviderCard
            provider={SLACK}
            authType="oauth2"
            credentials={SLACK_NEEDS_REAUTH}
            canManage={true}
            scope={PERSONAL}
            showMethod={false}
            alsoAtOrg={false}
          />
        </Section>

        <Section title="Same provider, two auth methods (method subtitle)">
          {/* Datadog appears twice — once per auth method. `showMethod`
              surfaces the method subtitle so the two cards are distinguishable. */}
          <div className="space-y-4">
            <ProviderCard
              provider={DATADOG_OAUTH_MANUAL}
              authType="oauth2_manual"
              credentials={DATADOG_OAUTH_CONNECTED}
              canManage={true}
              scope={PERSONAL}
              showMethod={true}
              alsoAtOrg={false}
            />
            <ProviderCard
              provider={DATADOG_API_KEY}
              authType="api_key"
              credentials={DATADOG_API_KEY_CONNECTED}
              canManage={true}
              scope={PERSONAL}
              showMethod={true}
              alsoAtOrg={false}
            />
          </div>
        </Section>

        <Section title="aws-sso session credentials (displayName, no rename/reconnect)">
          {/* Login + role sessions. The login row must read "AWS IAM Identity
              Center — host" (not "Untitled account"), and every row's ⋯ menu
              must omit Rename and Reconnect (type === 'session'). */}
          <ProviderCard
            provider={AWS_SSO}
            authType="oauth2"
            credentials={AWS_SSO_SESSIONS}
            canManage={true}
            scope={PERSONAL}
            showMethod={false}
            alsoAtOrg={false}
          />
        </Section>

        <Section title="Read-only view (member on a space tab they don't admin)">
          <ProviderCard
            provider={ATLASSIAN}
            authType="oauth2"
            credentials={ATLASSIAN_ONE}
            canManage={false}
            scope={{
              kind: 'space',
              spaceId: 'spc-eng',
              spaceName: 'Engineering',
            }}
            showMethod={false}
            alsoAtOrg={false}
          />
        </Section>

        <Section title="Inline `Also available at org level` hint">
          <ProviderCard
            provider={GOOGLE}
            authType="oauth2"
            credentials={GOOGLE_TWO_DEFAULTED}
            canManage={true}
            scope={PERSONAL}
            showMethod={false}
            alsoAtOrg={true}
          />
        </Section>

        <Section title="Available providers (disconnected)">
          {/* Datadog appears as two rows (oauth_manual + api_key) — each keeps
              its own method subtitle since the provider id occurs twice.
              Atlassian is a single catalog entry advertising BOTH oauth2 and
              api_key; the list expands it into two rows ("Connect" + "Add API
              token") so neither method is unreachable. */}
          <AvailableProvidersList
            providers={[ATLASSIAN, TRELLO, GITLAB, X, DATADOG_OAUTH_MANUAL, DATADOG_API_KEY]}
            scope={PERSONAL}
            canManage={true}
            connectedKeys={new Set()}
          />
        </Section>

        <Section title="Org scope (API-key only — OAuth-only providers filtered)">
          {/* X is OAuth-only and should be filtered out; Trello and GitLab
              support API keys and should render with no OAuth option. */}
          <AvailableProvidersList
            providers={[TRELLO, GITLAB, X]}
            scope={ORG}
            canManage={true}
            connectedKeys={new Set()}
          />
        </Section>

        <Section title="Add API Key modal (Atlassian / Trello / GitLab)">
          <div className="flex gap-2 flex-wrap">
            <Button onClick={() => setModalProvider(ATLASSIAN)}>
              Atlassian (email + siteUrl)
            </Button>
            <Button onClick={() => setModalProvider(TRELLO)}>
              Trello (app key)
            </Button>
            <Button onClick={() => setModalProvider(GITLAB)}>
              GitLab (optional gitlabUrl)
            </Button>
            <Button onClick={() => setModalProvider(SLACK_APP)}>
              Slack bot token (xoxb + scopes)
            </Button>
          </div>
        </Section>

        <Section title="Device-auth modal (aws-sso device flow)">
          {/* aws-sso `/start` returns a verificationUriComplete + userCode
              instead of an authorizationUrl; this modal shows the code and an
              "Open sign-in" button rather than opening a blank tab. */}
          <Button onClick={() => setDeviceOpen(true)}>
            Open device-auth modal
          </Button>
        </Section>
      </div>

      {modalProvider && (
        <AddApiKeyModal
          isOpen={true}
          onClose={() => setModalProvider(null)}
          provider={modalProvider}
          scope={PERSONAL}
        />
      )}
      {deviceOpen && (
        <DeviceAuthModal
          isOpen={true}
          onClose={() => setDeviceOpen(false)}
          provider={AWS_SSO}
          device={AWS_SSO_DEVICE}
        />
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h2>
      {children}
    </section>
  );
}
